import { useEffect, useState } from "react";
import { BookPlus, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import Markdown from "react-markdown";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { manualUrl } from "@/components/HelpButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const btn = "btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50";

export default function FaqDraftDialog({ item, onClose, onAdded }) {
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setDraft(null); setError("");
    api.post("/admin/help/faq/draft", { question: item.question, answer: item.answer || "" })
      .then((r) => setDraft(r.data))
      .catch((e) => { setError(formatApiError(e.response?.data?.detail)); setDraft({ question: item.question, answer: item.answer || "" }); });
  }, [item]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/help/faq", { question: draft.question, answer: draft.answer, source_question: item.question });
      toast.success("FAQ-regel toegevoegd aan de handleiding.", { action: { label: "Bekijk", onClick: () => window.open(manualUrl("faq"), "_blank") } });
      onAdded?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl border-2 border-foreground rounded-none heavy-shadow max-h-[85vh] overflow-y-auto" data-testid="faq-draft-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight pr-8 inline-flex items-center gap-2"><BookPlus className="w-5 h-5" /> Toevoegen aan Veelgestelde vragen</DialogTitle>
          <DialogDescription>De AI herschrijft de chatvraag tot een algemene FAQ-regel. Controleer en pas aan, daarna wordt de regel onderaan het hoofdstuk <i>Veelgestelde vragen</i> van de handleiding gezet.</DialogDescription>
        </DialogHeader>
        {item && (
          <div className="text-xs bg-muted/40 border border-border p-2" data-testid="faq-draft-source">
            <span className="font-display uppercase tracking-widest text-[10px] text-muted-foreground">{item.gap ? "Kennisgat — schrijf zelf het antwoord" : `Gesteld ${item.count}×`}</span>
            <div className="mt-0.5">“{item.question}”</div>
          </div>
        )}
        {!draft ? (
          <div className="flex items-center gap-2 text-sm py-6 justify-center" data-testid="faq-draft-loading"><Loader2 className="w-4 h-4 animate-spin" /> <Sparkles className="w-4 h-4" /> AI stelt de FAQ-tekst op…</div>
        ) : (
          <div className="space-y-3">
            {error && <div className="text-xs text-destructive" data-testid="faq-draft-error">{error} — je kunt de tekst hieronder zelf schrijven.</div>}
            <label className="block">
              <span className="font-display uppercase text-[10px] tracking-widest">Vraag</span>
              <input value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} maxLength={300} data-testid="faq-draft-question"
                className="w-full border-2 border-foreground bg-background px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary" />
            </label>
            <label className="block">
              <span className="font-display uppercase text-[10px] tracking-widest">Antwoord (Markdown)</span>
              <textarea value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} rows={5} maxLength={4000} data-testid="faq-draft-answer"
                placeholder={item?.gap ? "Schrijf hier het antwoord dat in de handleiding hoort…" : ""}
                className="w-full border-2 border-foreground bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </label>
            <div className="border-2 border-dashed border-border p-3 text-sm" data-testid="faq-draft-preview">
              <div className="font-display uppercase text-[10px] tracking-widest text-muted-foreground mb-1">Voorbeeld in de handleiding</div>
              <p className="font-bold">{draft.question}</p>
              <div className="prose prose-sm max-w-none"><Markdown>{draft.answer}</Markdown></div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className={`${btn} bg-background text-foreground`} data-testid="faq-draft-cancel">Annuleren</button>
              <button onClick={save} disabled={saving || draft.question.trim().length < 3 || draft.answer.trim().length < 3} className={`${btn} bg-primary text-primary-foreground`} data-testid="faq-draft-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Opslaan in handleiding
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
